// src/components/myAccount/pages/BillingHistoryPage.js
import React, { useState, useEffect } from 'react';
import { 
  CreditCard, 
  Download, 
  Calendar, 
  DollarSign, 
  CheckCircle, 
  AlertCircle,
  Clock,
  Receipt,
  Search,
  Eye,
  Crown,
  Shield,
  Users,
  ArrowRight
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import stripeService from '../../../services/stripeService';
import { supabase } from '../../../config';

const BillingHistoryPage = ({ user, userRoles, onRefreshData }) => {
  const [billingHistory, setBillingHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [subscriptionMap, setSubscriptionMap] = useState({}); // Map subscription_id to plan info
  const navigate = useNavigate();

  useEffect(() => {
    if (user?.id) {
      initializeData();
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch current subscription
  const fetchCurrentSubscription = async () => {
    if (!user?.id) return;
    
    try {
      const { data, error } = await supabase
        .from('subscriptions')
        .select('plan_type, status, trial_ends_at, stripe_subscription_id, billing_cycle, amount_cents, expires_at')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      
      if (!error && data && data.status && !['canceled'].includes(data.status)) {
        setCurrentSubscription(data);
      } else {
        setCurrentSubscription(null);
      }
    } catch (err) {
      console.error('Error fetching subscription:', err);
      setCurrentSubscription(null);
    }
  };

  // Build subscription map from billing history
  const buildSubscriptionMap = async (history) => {
    const subscriptionIds = new Set();
    history.forEach(invoice => {
      // Handle subscription_id from Stripe (could be string or object)
      let subId = invoice.subscription_id;
      if (subId) {
        if (typeof subId === 'object' && subId.id) {
          subId = subId.id;
        }
        if (typeof subId === 'string' && subId.startsWith('sub_')) {
          subscriptionIds.add(subId);
        }
      }
    });

    if (subscriptionIds.size === 0) {
      setSubscriptionMap({});
      return;
    }

    try {
      const { data, error } = await supabase
        .from('subscriptions')
        .select('stripe_subscription_id, plan_type, billing_cycle')
        .in('stripe_subscription_id', Array.from(subscriptionIds));

      if (!error && data) {
        const map = {};
        data.forEach(sub => {
          if (sub.stripe_subscription_id) {
            map[sub.stripe_subscription_id] = {
              plan_type: sub.plan_type,
              billing_cycle: sub.billing_cycle
            };
          }
        });
        setSubscriptionMap(map);
      }
    } catch (err) {
      console.error('Error building subscription map:', err);
    }
  };

  const initializeData = async () => {
    try {
      setLoading(true);
      
      // Fetch subscription and billing history in parallel
      await Promise.all([
        fetchCurrentSubscription(),
        (async () => {
          // Get customer ID
          const cId = await stripeService.getCustomerId(user.id);
          
          if (cId) {
            await fetchBillingHistory(cId);
          } else {
            setBillingHistory([]);
          }
        })()
      ]);
    } catch (error) {
      console.error('Error initializing billing history:', error);
      setError('Failed to load billing history');
    } finally {
      setLoading(false);
    }
  };

  const fetchBillingHistory = async (cId) => {
    try {
      const response = await stripeService.getBillingHistory(cId);
      if (response.success) {
        const history = response.billing_history || [];
        setBillingHistory(history);
        // Build subscription map after fetching history
        await buildSubscriptionMap(history);
      }
    } catch (error) {
      console.error('Error fetching billing history:', error);
      setError('Failed to load billing history');
    }
  };

  const getPlanIcon = (planType) => {
    switch (planType) {
      case 'free': return Users;
      case 'basic': return Shield;
      case 'pro': return Crown;
      default: return CreditCard;
    }
  };

  const getPlanName = (planType) => {
    if (!planType) return 'N/A';
    return planType.charAt(0).toUpperCase() + planType.slice(1);
  };

  const handleDownloadInvoice = async (invoiceId, invoicePdf) => {
    try {
      if (invoicePdf) {
        // Open PDF in new tab
        window.open(invoicePdf, '_blank');
        setMessage('Opening invoice...');
        setTimeout(() => setMessage(''), 2000);
      } else {
        // Try to get PDF URL from API
        const response = await stripeService.getInvoicePdf(invoiceId);
        if (response.success && response.pdf_url) {
          window.open(response.pdf_url, '_blank');
          setMessage('Opening invoice...');
          setTimeout(() => setMessage(''), 2000);
        } else {
          setError('Invoice PDF not available');
        }
      }
    } catch (error) {
      console.error('Error downloading invoice:', error);
      setError('Failed to download invoice');
    }
  };

  const handleViewInvoice = async (invoiceId) => {
    try {
      const response = await stripeService.getInvoice(invoiceId);
      if (response.success && response.invoice.hosted_invoice_url) {
        window.open(response.invoice.hosted_invoice_url, '_blank');
      } else {
        setError('Invoice not available');
      }
    } catch (error) {
      console.error('Error viewing invoice:', error);
      setError('Failed to open invoice');
    }
  };

  const formatCurrency = (amount, currency = 'usd') => {
    if (amount === 0) return 'Free';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      
      const day = date.getDate();
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const month = monthNames[date.getMonth()];
      const year = date.getFullYear();
      
      return `${day} ${month} ${year}`;
    } catch {
      return '';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'open':
      case 'pending':
        return <Clock className="w-4 h-4 text-yellow-500" />;
      case 'failed':
      case 'void':
      case 'uncollectible':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      case 'draft':
        return <Clock className="w-4 h-4 text-gray-400" />;
      default:
        return <Clock className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return 'text-green-600 bg-green-50';
      case 'open':
      case 'pending':
        return 'text-yellow-600 bg-yellow-50';
      case 'failed':
      case 'void':
      case 'uncollectible':
        return 'text-red-600 bg-red-50';
      case 'draft':
        return 'text-gray-600 bg-gray-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const filteredHistory = billingHistory.filter(item => {
    const invoiceNumber = item.invoice_number || item.id || '';
    const description = item.description || '';
    const matchesSearch = invoiceNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || item.status === statusFilter;
    const matchesDate = dateFilter === 'all' || 
                       (dateFilter === 'last30' && new Date(item.date) > new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)) ||
                       (dateFilter === 'last90' && new Date(item.date) > new Date(Date.now() - 90 * 24 * 60 * 60 * 1000)) ||
                       (dateFilter === 'thisyear' && new Date(item.date).getFullYear() === new Date().getFullYear());
    
    return matchesSearch && matchesStatus && matchesDate;
  });

  const totalSpent = billingHistory
    .filter(item => item.status === 'paid')
    .reduce((sum, item) => sum + (item.amount || 0), 0);

  const thisMonthSpent = billingHistory
    .filter(item => {
      const itemDate = new Date(item.date);
      const currentDate = new Date();
      return item.status === 'paid' && 
             itemDate.getMonth() === currentDate.getMonth() && 
             itemDate.getFullYear() === currentDate.getFullYear();
    })
    .reduce((sum, item) => sum + (item.amount || 0), 0);

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Billing History</h1>
        <p className="text-gray-600">View and manage your billing history and invoices</p>
      </div>

      {/* Current Subscription Summary */}
      {currentSubscription && (
        <div className="mb-8 bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200 rounded-lg p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {(() => {
                const IconComponent = getPlanIcon(currentSubscription.plan_type);
                return <IconComponent className="h-10 w-10 text-blue-600" />;
              })()}
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Current Subscription</h3>
                <div className="flex items-center space-x-4 mt-1">
                  <span className="text-sm text-gray-700">
                    <span className="font-medium">Plan:</span> {getPlanName(currentSubscription.plan_type)}
                  </span>
                  {currentSubscription.billing_cycle && (
                    <span className="text-sm text-gray-700">
                      <span className="font-medium">Billing:</span> {currentSubscription.billing_cycle === 'yearly' ? 'Yearly' : 'Monthly'}
                    </span>
                  )}
                  {currentSubscription.amount_cents > 0 && (
                    <span className="text-sm text-gray-700">
                      <span className="font-medium">Amount:</span> {formatCurrency(currentSubscription.amount_cents)}
                      {currentSubscription.billing_cycle === 'yearly' ? '/year' : '/month'}
                    </span>
                  )}
                  <span className={`text-sm px-2 py-1 rounded-full ${
                    currentSubscription.status === 'active' || currentSubscription.status === 'trialing'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {currentSubscription.status === 'trialing' ? 'Trial' : 'Active'}
                  </span>
                </div>
              </div>
            </div>
            <button
              onClick={() => navigate('/my-account/subscriptions')}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
            >
              Manage Subscription
              <ArrowRight className="h-4 w-4 ml-2" />
            </button>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Spent</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(totalSpent)}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-full">
              <DollarSign className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">This Month</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(thisMonthSpent)}</p>
            </div>
            <div className="p-3 bg-green-100 rounded-full">
              <Calendar className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Invoices</p>
              <p className="text-2xl font-bold text-gray-900">{billingHistory.length}</p>
            </div>
            <div className="p-3 bg-purple-100 rounded-full">
              <Receipt className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search invoices..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="flex gap-4">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="paid">Paid</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
            </select>

            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Time</option>
              <option value="last30">Last 30 Days</option>
              <option value="last90">Last 90 Days</option>
              <option value="thisyear">This Year</option>
            </select>
          </div>
        </div>
      </div>

      {/* Message Display */}
      {message && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-green-800">{message}</p>
        </div>
      )}

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Billing History Table */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Invoice
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Plan
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Payment Method
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="8" className="px-6 py-12 text-center">
                    <div className="flex justify-center items-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                      <span className="ml-2 text-gray-600">Loading billing history...</span>
                    </div>
                  </td>
                </tr>
              ) : filteredHistory.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-12 text-center">
                    <div className="text-gray-500">
                      <Receipt className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                      <p className="text-lg font-medium">No invoices found</p>
                      <p className="text-sm">No billing history matches your current filters</p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredHistory.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {item.invoice_number}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {formatDate(item.date)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {(() => {
                        // Extract subscription ID (handle string or object)
                        let subId = item.subscription_id;
                        if (subId && typeof subId === 'object' && subId.id) {
                          subId = subId.id;
                        }
                        
                        const subInfo = subId ? subscriptionMap[subId] : null;
                        if (subInfo) {
                          const IconComponent = getPlanIcon(subInfo.plan_type);
                          return (
                            <div className="flex items-center space-x-2">
                              <IconComponent className="h-4 w-4 text-gray-500" />
                              <span className="text-sm font-medium text-gray-900">
                                {getPlanName(subInfo.plan_type)}
                              </span>
                              {subInfo.billing_cycle && (
                                <span className="text-xs text-gray-500">
                                  ({subInfo.billing_cycle})
                                </span>
                              )}
                            </div>
                          );
                        } else if (subId) {
                          return (
                            <span className="text-sm text-gray-500 italic">Subscription</span>
                          );
                        } else {
                          return (
                            <span className="text-sm text-gray-400 italic">One-time</span>
                          );
                        }
                      })()}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{item.description}</div>
                      {item.period_start && item.period_end && (
                        <div className="text-sm text-gray-500">
                          {formatDate(item.period_start)} - {formatDate(item.period_end)}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {formatCurrency(item.amount, item.currency)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(item.status)}`}>
                        {getStatusIcon(item.status)}
                        <span className="ml-1 capitalize">{item.status}</span>
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-900">
                        <CreditCard className="w-4 h-4 mr-2" />
                        {item.payment_method}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleViewInvoice(item.id)}
                          className="text-blue-600 hover:text-blue-900 flex items-center"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </button>
                        <button
                          onClick={() => handleDownloadInvoice(item.id)}
                          className="text-green-600 hover:text-green-900 flex items-center"
                        >
                          <Download className="w-4 h-4 mr-1" />
                          Download
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination would go here if needed */}
      {filteredHistory.length > 0 && (
        <div className="mt-6 flex justify-between items-center">
          <p className="text-sm text-gray-700">
            Showing {filteredHistory.length} of {billingHistory.length} invoices
          </p>
        </div>
      )}
    </div>
  );
};

export default BillingHistoryPage;
